import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { searchSpecies, getTopSpecies, getSpeciesDetails } from '../api';

// Leaflet marker fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const thumbUrl = (f) => `/uploads/photos/${f}_thumb.jpg`;

// Zoom ↔ geschatte zichtbare straal
const ZOOM_KM = { 4:'2000 km', 5:'1000 km', 6:'500 km', 7:'200 km', 8:'100 km', 9:'75 km', 10:'50 km', 11:'25 km', 12:'10 km', 13:'5 km', 14:'2 km' };

// Reageert op slider-wijzigingen zonder de kaart te hermonteren
function ZoomController({ zoom }) {
  const map = useMap();
  useEffect(() => { map.setZoom(zoom); }, [zoom]);
  return null;
}

export default function SpeciesPage() {
  const navigate = useNavigate();
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [top, setTop]               = useState([]);
  const [loading, setLoading]       = useState(false);

  // Details per soort (lazy geladen) — sleutel is latin_name (of dutch_name als fallback)
  const [details, setDetails]       = useState({}); // { [latin_name|dutch_name]: { photos, locations } }
  const [loadingDetails, setLoadingDetails] = useState({});

  // Kaart-overlay
  const [mapSpecies, setMapSpecies] = useState(null); // { dutch_name, latin_name, locations }
  const [mapZoom,    setMapZoom]    = useState(10);   // ~50 km standaard

  useEffect(() => {
    getTopSpecies().then(setTop).catch(() => {});
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try { setResults(await searchSpecies(q)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const loadDetails = async (species) => {
    const key = species.latin_name || species.dutch_name;
    if (details[key] || loadingDetails[key]) return;
    setLoadingDetails(p => ({ ...p, [key]: true }));
    try {
      const d = await getSpeciesDetails(species.latin_name, species.dutch_name);
      setDetails(p => ({ ...p, [key]: d }));
    } catch {}
    finally { setLoadingDetails(p => ({ ...p, [key]: false })); }
  };

  const openMap = async (species) => {
    const key = species.latin_name || species.dutch_name;
    if (!details[key]) await loadDetails(species);
    setMapSpecies({ ...species, locations: details[key]?.locations || [] });
  };

  const showList = query.length >= 2 ? results : top;
  const isTop    = query.length < 2;

  return (
    <div className="species-page">
      <h2 style={{ fontWeight: 700, fontSize: 20, marginBottom: 20 }}>🐟 Soorten</h2>

      {/* Zoekbalk */}
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Zoek op Nederlandse of Latijnse naam…"
          style={{
            width: '100%', padding: '10px 40px 10px 14px',
            borderRadius: 10, border: '1.5px solid var(--border)',
            fontSize: 14, background: 'var(--surface)', color: 'var(--text)',
            boxSizing: 'border-box',
          }}
          autoFocus
        />
        {loading && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>⏳</span>}
        {!loading && query && (
          <button onClick={() => setQuery('')}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}>✕</button>
        )}
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
        {isTop ? `${top.length} meest geziene soorten` : `${results.length} resultaten voor "${query}"`}
      </div>

      {showList.length === 0 && !loading && query.length >= 2 && (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          Geen soorten gevonden voor "{query}"
        </div>
      )}

      <div className="species-grid">
        {showList.map((s, i) => {
          const key    = s.latin_name || s.dutch_name;
          const det    = details[key];
          const hasGps = det?.locations?.length > 0;

          // Laad details bij zichtbaarheid
          if (!det && !loadingDetails[key]) loadDetails(s);

          return (
            <div key={i} className="card" style={{ padding: '14px 16px' }}>

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>🐟 {s.dutch_name}</div>
                  {s.latin_name && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 2 }}>
                      {s.latin_name}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {/* Kaart-knop */}
                  <button
                    onClick={() => openMap(s)}
                    title={hasGps ? 'Toon op kaart' : 'Geen GPS-data beschikbaar'}
                    style={{
                      background: hasGps ? 'var(--blue-light)' : 'var(--border)',
                      color: hasGps ? 'var(--blue)' : 'var(--text-muted)',
                      border: 'none', borderRadius: 8, padding: '4px 10px',
                      fontSize: 14, cursor: hasGps ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                    🗺️
                  </button>
                  <span style={{
                    background: 'var(--blue-light)', color: 'var(--blue)',
                    borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 700,
                  }}>
                    {s.dive_count} duik{s.dive_count !== 1 ? 'en' : ''}
                  </span>
                </div>
              </div>

              {/* Thumbnails */}
              {det?.photos?.length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                  {det.photos.slice(0, 8).map((p, j) => (
                    <div key={j} onClick={() => navigate(`/dives/${p.dive_id}`)}
                      title={`${p.site_name || ''} · ${p.dive_date?.slice(0,10) || ''}`}
                      style={{ cursor: 'pointer', borderRadius: 6, overflow: 'hidden', width: 64, height: 64, flexShrink: 0 }}>
                      <img src={thumbUrl(p.filename)} alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </div>
                  ))}
                </div>
              )}
              {loadingDetails[key] && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>⏳ Laden…</div>
              )}

              {/* Duiken */}
              {s.dive_ids?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {s.dive_ids.slice(0, 6).map((id, j) => (
                    <button key={id} onClick={() => navigate(`/dives/${id}`)}
                      style={{
                        padding: '3px 10px', borderRadius: 99, fontSize: 11,
                        border: '1.5px solid var(--border)', background: 'transparent',
                        color: 'var(--text-muted)', cursor: 'pointer',
                      }}>
                      🤿 #{id}{s.dates?.[j] ? ` · ${s.dates[j].slice(0,10)}` : ''}
                    </button>
                  ))}
                  {s.dive_ids.length > 6 && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', padding: '3px 0' }}>
                      +{s.dive_ids.length - 6} meer
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Kaart-overlay */}
      {mapSpecies && (
        <div
          onClick={() => setMapSpecies(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,.7)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 14,
              width: '100%', maxWidth: 700, overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,.4)',
            }}
          >
            {/* Header */}
            <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>🗺️ {mapSpecies.dutch_name}</div>
                {mapSpecies.latin_name && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>{mapSpecies.latin_name}</div>
                )}
              </div>
              <button onClick={() => setMapSpecies(null)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
            </div>

            {/* Kaart */}
            {mapSpecies.locations?.length > 0 ? (
              <div style={{ height: 400 }}>
                <MapContainer
                  key={mapSpecies.dutch_name}
                  center={[
                    mapSpecies.locations.reduce((s, l) => s + parseFloat(l.lat), 0) / mapSpecies.locations.length,
                    mapSpecies.locations.reduce((s, l) => s + parseFloat(l.lng), 0) / mapSpecies.locations.length,
                  ]}
                  zoom={mapZoom}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom
                >
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="Esri"
                  />
                  <ZoomController zoom={mapZoom} />
                  {mapSpecies.locations.map((loc, i) => (
                    <Marker key={i} position={[parseFloat(loc.lat), parseFloat(loc.lng)]}>
                      <Popup>
                        <strong>{loc.site_name || 'Onbekende locatie'}</strong><br />
                        {loc.dive_date?.slice(0, 10)}<br />
                        <button
                          onClick={() => { setMapSpecies(null); navigate(`/dives/${loc.dive_id}`); }}
                          style={{ marginTop: 4, fontSize: 11, cursor: 'pointer' }}>
                          🤿 Naar duik #{loc.dive_id}
                        </button>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                Geen GPS-coördinaten beschikbaar voor deze duiken.
              </div>
            )}

            {/* Zoom slider */}
            {mapSpecies.locations?.length > 0 && (
              <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13 }}>🔍</span>
                <input
                  type="range" min={4} max={14} step={1} value={mapZoom}
                  onChange={e => setMapZoom(Number(e.target.value))}
                  style={{ flex: 1, accentColor: 'var(--blue)', cursor: 'pointer' }}
                />
                <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 52, textAlign: 'right', flexShrink: 0 }}>
                  ~{ZOOM_KM[mapZoom]}
                </span>
              </div>
            )}

            {/* Locatielijst */}
            {mapSpecies.locations?.length > 0 && (
              <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {mapSpecies.locations.map((loc, i) => (
                  <span key={i}
                    onClick={() => { setMapSpecies(null); navigate(`/dives/${loc.dive_id}`); }}
                    style={{
                      fontSize: 11, padding: '3px 10px', borderRadius: 99, cursor: 'pointer',
                      background: 'var(--blue-light)', color: 'var(--blue)',
                    }}>
                    📍 {loc.site_name || `Duik #${loc.dive_id}`}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
